<template>
  <div>
    <LibraryRedesignBanner
      v-if="savedPLCollectionName || hasAccess === false"
      :title="savedPLCollectionName"
      :background-color="libraryBreadcrumbBackgroundColor">
      <template v-if="hasAccess" v-slot:breadcrumbList>
        <li><a :href="libraryFolder(savedPLCollectionUuid)"><i class="fa fa-folder header-folder" /> {{ savedPLCollectionName }}</a></li>
        <li class="active">Present Levels: {{ studentName }} ({{ gradeLabel }})</li>
      </template>
    </LibraryRedesignBanner>
    <NoAccessError v-if="hasAccess === false"/>
    <div v-else class="container">
      <div v-if="!savedPLCollectionName" class="row">
        <div class="col-md-offset-2 col-md-8">
          <h1 class="text-center">Present Levels Wizard</h1>
          <p class="text-center lead pl-subtitle">Use the wizard to identify specific areas of strengths or needs for an individual student.</p>
        </div>
      </div>
      <div v-if="!savedPLCollectionName && !uuid" class="row">
        <div class="col-md-offset-1 col-md-10 col-lg-offset-2 col-lg-8">
          <p class="browse-assessments-banner">Still need to gather student data?
            <router-link
              class="underline"
              :to="{name: 'BrowseAssessments'}"
              @click.native="sendBrowseEvent">Browse Assessments
            </router-link>
          </p>
        </div>
      </div>

      <div v-if="uuid">
        <present-levels-breadcrumbs
          v-if="shouldShowPlwBreadcrumbs"
          :uuid="uuid"
          :available-stages="availableStages"/>
        <div class="row">
          <div :class="!isConclusion && 'col-xs-12 col-md-9 col-lg-8'">
            <router-view :uuid="uuid"/>
          </div>

          <div class="col-xs-12 col-md-3 col-lg-4" v-if="!isConclusion">
            <save-to-library-panel
              :content-uuid="uuid"
              :content-type="contentType"
              :content-last-save="lastUpdate.toString()"
              :content-saving="saving"
              :collection-uuid="savedPLCollectionUuid"
              :collection-name="savedPLCollectionName"
              :content="{demo: false}"
              @saved:success="refreshCurrentContentFolder"
              @removed:success="refreshCurrentContentFolder"
            />
          </div>
        </div>
      </div>
      <div v-else class="row">
        <div class="col-md-offset-1 col-md-10 col-lg-offset-2 col-lg-8">
          <router-view/>
        </div>
      </div>

      <submit-feedback type="Present Levels Wizard" />
    </div>
    <LimitActionModal
      v-if="blockerPermissions"
      :show.sync="showBlockerModal"
      :action-permissions="blockerPermissions" />
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'
import LibraryBanner from '@/components/library/LibraryBanner'
import LibraryRedesignBanner from '@/components/library/LibraryRedesignBanner'
import SaveToLibraryPanel from '@/components/presentlevels/SaveToLibraryPanel'
import PresentLevelsBreadcrumbs from '@/components/presentlevels/PresentLevelsBreadcrumbs'
import SubmitFeedback from '@/components/SubmitFeedback'
import LimitActionModal from '@/components/LimitActionModal'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { noSelMixin } from '@/contentprofile/no_sel_mixins'
import { toolkitUrls } from '@/links'
import { getGradeLabelForGradeLevel } from '@/api/presentlevels'
import NoAccessError from '@/components/NoAccessError.vue'

export default {
  name: 'PresentLevels',
  components: {
    NoAccessError,
    LibraryBanner,
    LibraryRedesignBanner,
    SaveToLibraryPanel,
    PresentLevelsBreadcrumbs,
    SubmitFeedback,
    LimitActionModal
  },
  mixins: [noSelMixin],
  data() {
    return {
      contentType: CONTENT_TYPES.PRESENTLEVEL,
      draftedPLEventSent: false,
      ...toolkitUrls
    }
  },
  computed: {
    ...mapState('presentlevels', {
      uuid: state => state.breadcrumbsInfo.uuid,
      availableStages: state => state.breadcrumbsInfo.availableStages,
      saving: state => state.saving,
      lastUpdate: state => state.lastUpdate,
      enrolledGrade: state => state.enrolledGrade,
      studentName: state => state.studentName,
      areasOfNeed: state => state.areasOfNeed,
      showBlocker: state => state.showBlocker,
      blockerPermissions: state => state.blockerPermissions,
      hasAccess: state => state.hasAccess
    }),
    ...mapState('library', {
      savedPLCollectionUuid: state => state.currentContentFolder.folderUuid,
      savedPLCollectionName: state => state.currentContentFolder.folderName,
    }),
    ...mapGetters('library', ['canEditCurrentContent']),
    ...mapState('user', { userUuid: state => state.user.uuid }),
    ...mapGetters('visibility', ['betaVisibility']),
    libraryParams() {
      return {
        userUuid: this.userUuid,
        contentUuid: this.uuid,
        contentType: this.contentType
      }
    },
    showBlockerModal: {
      get() {
        return this.showBlocker
      },
      set(flag) {
        this.setShowBlocker(flag)
      }
    },
    isConclusion() {
      return this.$route.name.startsWith('PresentlevelsConclusion')
    },
    availableStagesLabels() {
      return this.getPlAreaLabelsFromValues(this.availableStages)
    },
    getBrowseAssessmentsRoute() {
      let route = {}
      route.name = this.betaVisibility ? 'BrowseAllAssessments' : 'BrowseAssessments'
      if (this.betaVisibility) {
        route.query = {beta: true}
      }
      return route
    },
    plStudentInfo() {
      return {
        enrolledGrade: this.enrolledGrade,
        areasOfNeed: this.areasOfNeed,
        studentCollectionName: this.savedPLCollectionName,
        studentCollectionUuid: this.savedPLCollectionUuid
      }
    },
    gradeLabel() {
      return getGradeLabelForGradeLevel(this.enrolledGrade)
    },
    shouldShowPlwBreadcrumbs() {
      if (this.hasAccess === false) {
        return false
      }
      const isSaved = this.savedPLCollectionUuid !== ''
      if (!isSaved) {
        return true
      }
      if (!this.canEditCurrentContent) {
        return false
      }
      return this.availableStages && this.availableStages.length > 0
    },
    libraryBreadcrumbBackgroundColor() {
      if (this.shouldShowPlwBreadcrumbs) {
        return 'transparent'
      }
      return 'gray'
    }
  },
  eventContext() {
    return {
      plw_enrolled_grade: this.enrolledGrade,
      plw_areas_of_need: this.areasOfNeed,
      plw_is_library: !!this.savedPLCollectionName,
      is_library: !!this.savedPLCollectionName,
      student_uuid: this.savedPLCollectionUuid
    }
  },
  methods: {
    ...mapActions('library', ['fetchCurrentContentFolder']),
    ...mapMutations('library', ['setCurrentContentFolder', 'clearCurrentContentFolder']),
    ...mapMutations('presentlevels', ['resetState', 'setShowBlocker']),
    refreshCurrentContentFolder() {
      if (this.libraryParams && this.libraryParams.userUuid && this.libraryParams.contentUuid) {
        this.fetchCurrentContentFolder(this.libraryParams)
      }
    },
    sendBrowseEvent() {
      this.$sendEvent('Clicked Browse Assessments from Present Levels')
    }
  },
  created() {
    this.resetState(null)
    this.$sendEvent('Viewed Present Levels Wizard', {
      ref: this.$route.query.ref
    })
  },
  beforeDestroy() {
    this.clearCurrentContentFolder()
  },
  watch: {
    libraryParams(libraryParams) {
      if (libraryParams.userUuid && libraryParams.contentUuid) {
        this.fetchCurrentContentFolder(libraryParams)
      }
    },
    plStudentInfo(newVal, oldVal) {
      if (this.draftedPLEventSent) { // Only send this event once
        return
      }
      const {enrolledGrade, areasOfNeed, studentCollectionName, studentCollectionUuid} = newVal
      if (enrolledGrade && areasOfNeed && studentCollectionName && studentCollectionUuid) {
        this.$sendEvent('Viewed Drafted Present Levels')
        this.draftedPLEventSent = true
      }
    }
  }
}
</script>

<style scoped lang="scss">
.pl-subtitle {
  font-size: 18px;
}
.browse-assessments-banner {
  font-size: 1.6rem;
  text-align: left;
  padding-top: 20px;
  padding-bottom: 10px;
}
</style>
